import Modal from 'chameleon/ui-stack/ui/base/modal';
import Button from 'chameleon/ui-stack/ui/base/button';
import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const ConfirmDialog = (props) => {
  const {
    children,
    title,
    subTitle,
    topBar,
    dialogClassName,
    wrapperClassName,
    contentClassName,
    headingClassName,
    topBarClassName,
    showCloseButton,
    content,
    onSuccess,
    onClose,
    onSecondaryBtnClick,
    isPrimaryBtnDisabled,
    contentLabel,
    aria,
    contentWithSpacing,
    size,
    isContainingBlock,
    isScrollableContent,
    hasTitleWithTooltip,
    headerWithBorder,
    alignItemsBottomContent,
  } = props;
  const {
    btnPrimary,
    btnPrimaryType = 'primary',
    btnPrimaryAria = {},
    btnSecondaryType = 'secondary',
    btnSecondary,
    bottomLeftContent,
  } = content || {};

  const _onClose = (event) => {
    onClose(event);
  };

  const isPrimaryBtn = btnPrimaryType === 'primary';
  const isPrimaryDangerBtn = btnPrimaryType === 'danger';
  const isPrimarySuccessBtn = btnPrimaryType === 'success';
  const isSecondaryBtn = btnSecondaryType === 'secondary';
  const isSecondaryDangerBtn = btnSecondaryType === 'danger';
  const alignButtons = alignItemsBottomContent || 'end';

  return createPortal(
    <Modal
      title={title}
      subTitle={subTitle}
      topBar={topBar}
      size={size}
      onClose={_onClose}
      dialogClassName={dialogClassName}
      wrapperClassName={wrapperClassName}
      contentClassName={contentClassName}
      headingClassName={headingClassName}
      topBarClassName={topBarClassName}
      showCloseButton={showCloseButton}
      contentLabel={contentLabel}
      aria={aria}
      contentWithSpacing={contentWithSpacing}
      isContainingBlock={isContainingBlock}
      isScrollableContent={isScrollableContent}
      hasTitleWithTooltip={hasTitleWithTooltip}
      headerWithBorder={headerWithBorder}
      bottomContent={
        (btnSecondary || btnPrimary || bottomLeftContent) && (
          <div
            className={`footer flex gap-x-4 justify-${alignButtons} mt-auto p-6 border-t border-gray-300`}
          >
            {bottomLeftContent}
            {btnSecondary && (
              <Button
                isNaked
                isSecondary={isSecondaryBtn}
                isDanger={isSecondaryDangerBtn}
                accessibility={{
                  'data-test-id': 'button-secondary',
                  ...btnPrimaryAria,
                }}
                onClick={onSecondaryBtnClick || onClose}
              >
                {btnSecondary}
              </Button>
            )}

            {btnPrimary && (
              <Button
                isPrimary={isPrimaryBtn}
                isSuccess={isPrimarySuccessBtn}
                isDanger={isPrimaryDangerBtn}
                disabled={isPrimaryBtnDisabled}
                accessibility={{
                  'data-test-id': 'button-primary',
                }}
                onClick={onSuccess}
              >
                {btnPrimary}
              </Button>
            )}
          </div>
        )
      }
    >
      {children}
    </Modal>,
    document.querySelector('#modal'),
  );
};

ConfirmDialog.propTypes = {
  // The title of the modal
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The title of the modal
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // Places content in the top bar between title and X
  topBar: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The class to style the dialog window.
  dialogClassName: PropTypes.string,
  // The class to style the modal content.
  contentClassName: PropTypes.string,
  // The class to style the modal wrapper.
  wrapperClassName: PropTypes.string,
  // The class to style the modal heading title.
  headingClassName: PropTypes.string,
  // The class to style the modal top bar.
  topBarClassName: PropTypes.string,
  // Handler for displaying X button.
  showCloseButton: PropTypes.bool,
  // Callback for submitting when confirmation buttons are available.
  onSuccess: PropTypes.func,
  // Callback for closing the modal and X button
  onClose: PropTypes.func,
  // Callback for clicking the secondary button
  onSecondaryBtnClick: PropTypes.func,
  // The accessibility content label
  contentLabel: PropTypes.string,
  // Object for setting accessibility aria options
  aria: PropTypes.object,
  // If true the content receives spacing tailwind classes (margins, paddings)
  contentWithSpacing: PropTypes.bool,
  // Sets the size of the modal
  size: PropTypes.string,
  // Sets the modal buttons options
  content: PropTypes.object,
  // Disables the primary button
  isPrimaryBtnDisabled: PropTypes.bool,
  // Transforms the wrapper container into a Containing Block
  isContainingBlock: PropTypes.bool,
  // If true it removes the scrolls from modal body
  isScrollableContent: PropTypes.bool,
  // If true the title tooltip algorithm is used to determine
  // if title needs tooltip
  hasTitleWithTooltip: PropTypes.bool,
  alignItemsBottomContent: PropTypes.oneOf(['end', 'between']), // between put the buttons with space between them.
};

ConfirmDialog.defaultProps = {
  title: <div />,
  topBar: null,
  dialogClassName: null,
  contentClassName: null,
  wrapperClassName: null,
  headingClassName: null,
  topBarClassName: null,
  showCloseButton: true,
  onSuccess: () => {},
  onClose: () => {},
  onSecondaryBtnClick: null,
  contentLabel: '',
  aria: {},
  contentWithSpacing: true,
  size: 'small',
  isPrimaryBtnDisabled: false,
  isScrollableContent: true,
  hasTitleWithTooltip: true,
};

export default ConfirmDialog;
